/**
 * Recurrence date math (SPEC §5.5). Pure calendar arithmetic on YYYY-MM-DD strings.
 */
import { addDays, addMonths, addYears, isDateString, monthLong, parseDate, startOfWeek, weekday, weekdayLong, formatDayShort } from './dates'
import type { Recurrence, Weekday } from './types'

/** First rule occurrence strictly after `from`, anchored on `from`. */
export function stepOnce(rule: Recurrence, from: string): string {
  const interval = Math.max(1, Math.floor(rule.interval || 1))
  switch (rule.freq) {
    case 'day':
      return addDays(from, interval)
    case 'week': {
      const days = normalizeWeekdays(rule.byWeekday)
      if (!days.length) return addDays(from, 7 * interval)
      // Anchor weeks on Monday so "every 2 weeks on Tue, Thu" is stable.
      const wd = weekday(from)
      const fromIdx = (wd + 6) % 7 // Mon=0..Sun=6
      const later = days.map((d) => (d + 6) % 7).filter((i) => i > fromIdx)
      if (later.length) return addDays(from, Math.min(...later) - fromIdx)
      const weekStart = startOfWeek(from, 1)
      const first = Math.min(...days.map((d) => (d + 6) % 7))
      return addDays(weekStart, 7 * interval + first)
    }
    case 'month': {
      const dom = rule.byMonthDay ?? parseDate(from).d
      return addMonths(from, interval, dom)
    }
    case 'year':
      return addYears(from, interval)
  }
}

function normalizeWeekdays(days?: Weekday[]): Weekday[] {
  if (!days) return []
  return [...new Set(days.filter((d) => Number.isInteger(d) && d >= 0 && d <= 6))].sort() as Weekday[]
}

/** Is `date` itself an occurrence of a weekly/monthly rule (used to align the first date)? */
export function matchesRule(rule: Recurrence, date: string): boolean {
  if (rule.freq === 'week') {
    const days = normalizeWeekdays(rule.byWeekday)
    return !days.length || days.includes(weekday(date))
  }
  if (rule.freq === 'month' && rule.byMonthDay) {
    const { y, m, d } = parseDate(date)
    const dim = new Date(Date.UTC(y, m, 0)).getUTCDate()
    return d === Math.min(rule.byMonthDay, dim)
  }
  return true
}

/**
 * Next date for a recurring item being checked off (SPEC §5.5 step 2).
 * - schedule: next rule occurrence after `current`, skipping dates before `today`.
 * - afterDone: next occurrence counted from `today`.
 * Returns null when the rule has ended (`until` passed).
 */
export function nextOccurrence(rule: Recurrence, current: string, today: string): string | null {
  let next: string
  if (rule.mode === 'afterDone') {
    next = stepOnce(rule, today)
  } else {
    next = stepOnce(rule, current)
    let guard = 0
    while (next < today && guard++ < 5000) next = stepOnce(rule, next)
  }
  if (rule.until && next > rule.until) return null
  return next
}

/** Advance without logging ("Skip this one"). Same math as schedule mode from the current date. */
export function skipOccurrence(rule: Recurrence, current: string, today: string): string | null {
  let next = stepOnce(rule, current)
  let guard = 0
  while (next < today && guard++ < 5000) next = stepOnce(rule, next)
  if (rule.until && next > rule.until) return null
  return next
}

/** Occurrences within [from, to] starting at `start` (for previews / ghost rows). */
export function occurrencesBetween(rule: Recurrence, start: string, from: string, to: string, limit = 60): string[] {
  const out: string[] = []
  let d = start
  let guard = 0
  while (d < from && guard++ < 5000) d = stepOnce(rule, d)
  while (d <= to && out.length < limit) {
    if (rule.until && d > rule.until) break
    out.push(d)
    d = stepOnce(rule, d)
  }
  return out
}

/**
 * The dates after `current` that a repeat on a schedule will land on, within [from, to] — the
 * "coming up" rows on Days. None for "after done" repeats: their next date depends on when
 * it's finished.
 */
export function upcomingOccurrences(rule: Recurrence, current: string, from: string, to: string, limit = 60): string[] {
  if (rule.mode === 'afterDone') return []
  const after = addDays(current, 1)
  return occurrencesBetween(rule, current, after > from ? after : from, to, limit)
}

const WD_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])!
}

/** "Every week on Tue", "Every 3 months (after done)", "Monthly on the 1st". */
export function describeRecurrence(rule: Recurrence): string {
  const n = Math.max(1, Number(rule.interval) || 1)
  const unit = ({ day: 'day', week: 'week', month: 'month', year: 'year' } as Record<string, string>)[rule.freq] ?? 'time'
  let s: string
  if (n === 1) {
    s = ({ day: 'Daily', week: 'Weekly', month: 'Monthly', year: 'Yearly' } as Record<string, string>)[rule.freq] ?? 'Repeats'
  } else {
    s = `Every ${n} ${unit}s`
  }
  if (rule.freq === 'week' && rule.byWeekday?.length) {
    s +=
      ' on ' +
      normalizeWeekdays(rule.byWeekday)
        .map((d) => WD_NAMES[d])
        .join(', ')
  }
  if (rule.freq === 'month' && rule.byMonthDay) s += ` on the ${ordinal(rule.byMonthDay)}`
  if (rule.mode === 'afterDone') s += ' after done'
  if (rule.until && isDateString(rule.until)) s += ` until ${formatDayShort(rule.until)}`
  return s
}

/** Sensible default rule for a date (UI "Repeat" presets). */
export function presetRule(kind: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'yearly' | 'weekdays', date: string): Recurrence {
  switch (kind) {
    case 'daily':
      return { freq: 'day', interval: 1, mode: 'schedule' }
    case 'weekdays':
      return { freq: 'week', interval: 1, byWeekday: [1, 2, 3, 4, 5], mode: 'schedule' }
    case 'weekly':
      return { freq: 'week', interval: 1, byWeekday: [weekday(date)], mode: 'schedule' }
    case 'biweekly':
      return { freq: 'week', interval: 2, byWeekday: [weekday(date)], mode: 'schedule' }
    case 'monthly':
      return { freq: 'month', interval: 1, byMonthDay: parseDate(date).d, mode: 'schedule' }
    case 'yearly':
      return { freq: 'year', interval: 1, mode: 'schedule' }
  }
}

/**
 * The repeat follows the date: when someone changes a repeating item's date, re-anchor the
 * rule so it keeps landing on that date's weekday / day of the month (the day is never set
 * twice). Weekly on one day → the new date's weekday. Weekly on several days → unchanged if
 * the new date is one of them, else the old date's day is swapped for the new one (or the new
 * one added). Monthly → the new date's day of the month. Daily and yearly already count from
 * the date. Returns the same object when nothing changes.
 */
export function alignRecurrence(rule: Recurrence, from: string | undefined, to: string): Recurrence {
  if (rule.freq === 'week') {
    const days = normalizeWeekdays(rule.byWeekday)
    const wd = weekday(to)
    if (!days.length || days.includes(wd)) return rule
    if (days.length === 1) return { ...rule, byWeekday: [wd] }
    const old = from && isDateString(from) ? weekday(from) : null
    const next = old !== null && days.includes(old) ? days.map((d) => (d === old ? wd : d)) : [...days, wd]
    return { ...rule, byWeekday: normalizeWeekdays(next) }
  }
  if (rule.freq === 'month' && rule.byMonthDay) {
    const d = parseDate(to).d
    return d === rule.byMonthDay ? rule : { ...rule, byMonthDay: d }
  }
  return rule
}

export type RepeatChoice = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom'

/** The simple choices in the date picker, each worded from the item's date. */
export function repeatChoices(date: string): Array<{ key: Exclude<RepeatChoice, 'none' | 'custom'>; label: string; rule: Recurrence }> {
  const { m, d } = parseDate(date)
  return [
    { key: 'daily', label: 'Every day', rule: presetRule('daily', date) },
    { key: 'weekly', label: `Every week on ${weekdayLong(date)}`, rule: presetRule('weekly', date) },
    { key: 'monthly', label: `Every month on the ${ordinal(d)}`, rule: presetRule('monthly', date) },
    { key: 'yearly', label: `Every year on ${monthLong(m)} ${d}`, rule: presetRule('yearly', date) },
  ]
}

/** Which simple choice a rule is for this date ('custom' for anything else). */
export function repeatChoiceOf(rule: Recurrence | undefined | null, date: string): RepeatChoice {
  if (!rule) return 'none'
  if (rule.mode !== 'schedule' || rule.until || (rule.interval || 1) !== 1) return 'custom'
  const days = normalizeWeekdays(rule.byWeekday)
  switch (rule.freq) {
    case 'day':
      return 'daily'
    case 'week':
      return days.length === 1 && days[0] === weekday(date) ? 'weekly' : 'custom'
    case 'month':
      return !rule.byMonthDay || rule.byMonthDay === parseDate(date).d ? 'monthly' : 'custom'
    case 'year':
      return 'yearly'
  }
}

/**
 * Short wording for a rule next to its date ("Every week", "Every 2 weeks", "Every weekday",
 * "Every week on Tue, Thu"): the day isn't repeated when it's the date's own.
 */
export function describeRepeat(rule: Recurrence, date?: string): string {
  const n = Math.max(1, Number(rule.interval) || 1)
  const unit = ({ day: 'day', week: 'week', month: 'month', year: 'year' } as Record<string, string>)[rule.freq] ?? 'time'
  let s = n === 1 ? `Every ${unit}` : `Every ${n} ${unit}s`
  const days = normalizeWeekdays(rule.byWeekday)
  if (rule.freq === 'week' && days.length) {
    const own = days.length === 1 && date && isDateString(date) && days[0] === weekday(date)
    if (n === 1 && days.join() === '1,2,3,4,5') s = 'Every weekday'
    else if (!own) s += ' on ' + days.map((d) => WD_NAMES[d]).join(', ')
  }
  if (rule.freq === 'month' && rule.byMonthDay && !(date && isDateString(date) && parseDate(date).d === rule.byMonthDay)) s += ` on the ${ordinal(rule.byMonthDay)}`
  if (rule.mode === 'afterDone') s += ' after done'
  if (rule.until && isDateString(rule.until)) s += ` until ${formatDayShort(rule.until)}`
  return s
}
